import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState,
} from "react"
import { Box, BoxProps } from "@chakra-ui/react"
import { Rect } from "./lib/viewport"
import { ObjectRules, useLiveObject, useObjectLayer } from "./ObjectLayer"
import { useSelection } from "./Editor"

export type Mirror = { horizontal: boolean; vertical: boolean }

type ObjectContextValue = {
  id: string
  selected: boolean
  rect: Rect
  rotation: number
  mirror: Mirror
  zIndex?: number
  hitTest?: ObjectRules["hitTest"]
  locked: boolean
  onSelect?: (id: string, additive: boolean) => void
  onRepeatClick?: () => void
  onContextMenu: (event: React.MouseEvent) => void
}

const ObjectContext = createContext<ObjectContextValue | null>(null)

export const useCanvasObject = () => {
  const context = useContext(ObjectContext)
  if (!context) throw new Error("CanvasObject parts must be used inside a CanvasObject")

  return context
}

type Props = Rect & {
  id: string
  rotation: number
  zIndex?: number
  selected: boolean
  /** Drawn mirrored; the handles stay where they are. */
  mirror?: Mirror
  /** No moving or resizing (while its text is edited, for instance). */
  locked?: boolean
  /** Whether a press lands on the object; outside it, the press goes to what's behind. */
  hitTest?: ObjectRules["hitTest"]
  onSelect?: (id: string, additive: boolean) => void
  /** A second click on the object within 400 ms (it's selected by the first). */
  onRepeatClick?: () => void
  onContextMenu?: (event: React.MouseEvent) => void
  children: React.ReactNode
}

const NO_MIRROR: Mirror = { horizontal: false, vertical: false }

// The canvas keeps the zoom in a CSS variable, so objects keep sizes on screen without
// re-rendering on every zoom step
const ZOOM = "var(--editor-zoom, 1)"
const ONE_SCREEN_PIXEL = `max(0.5px, calc(1px / ${ZOOM}))`
const REPEAT_CLICK_MS = 400
const CLICK_TOLERANCE_PX = 4

/**
 * One object on the canvas, at `x`, `y` in content units. It handles selecting; the object layer
 * moves, resizes and rotates it. Its parts draw it and say what it allows: `CanvasObjectBody`
 * (the object), `Resizable`, `Rotatable`, `CanvasObjectLabel`.
 */
const CanvasObject = ({
  id,
  x,
  y,
  width,
  height,
  rotation,
  zIndex,
  mirror = NO_MIRROR,
  selected,
  locked = false,
  hitTest,
  onSelect,
  onRepeatClick,
  onContextMenu,
  children,
}: Props) => {
  const { setRules } = useObjectLayer()
  useLayoutEffect(() => {
    setRules(id, { locked, hitTest })
  }, [setRules, id, locked, hitTest])

  // A right-click selects the object, then opens the menu
  const handleContextMenu = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!selected) onSelect?.(id, false)
    onContextMenu?.(event)
  }

  const value: ObjectContextValue = {
    id,
    selected,
    rect: {
      x, y, width, height,
    },
    rotation,
    mirror,
    zIndex,
    hitTest,
    locked,
    onSelect,
    onRepeatClick,
    onContextMenu: handleContextMenu,
  }

  return <ObjectContext.Provider value={value}>{children}</ObjectContext.Provider>
}

// Whole pixels, so borders don't straddle pixel boundaries
const pixelBox = (rect: Rect, scale: number) => ({
  left: `${Math.round(rect.x * scale)}px`,
  top: `${Math.round(rect.y * scale)}px`,
  width: `${Math.round(rect.width * scale)}px`,
  height: `${Math.round(rect.height * scale)}px`,
})

/** A press that stays a click: a second one within 400 ms repeats it. */
const useRepeatClick = (onRepeatClick?: () => void) => {
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (pending.current) clearTimeout(pending.current)
  }, [])

  return useCallback((down: MouseEvent) => {
    if (!onRepeatClick) return

    const onUp = (up: MouseEvent) => {
      document.removeEventListener("mouseup", onUp)
      const distance = Math.hypot(up.clientX - down.clientX, up.clientY - down.clientY)
      if (distance > CLICK_TOLERANCE_PX) return

      if (pending.current) {
        clearTimeout(pending.current)
        pending.current = null
        onRepeatClick()

        return
      }
      pending.current = setTimeout(() => {
        pending.current = null
      }, REPEAT_CLICK_MS)
    }
    document.addEventListener("mouseup", onUp)
  }, [onRepeatClick])
}

type BodyProps = Omit<BoxProps, "children"> & {
  /** Draws the object; gets the moved or resized rectangle while a gesture is in progress. */
  children: (rect: Rect) => React.ReactNode
}

/** The object itself: where it's drawn, and where the pointer grabs it. */
export const CanvasObjectBody = ({ children, ...boxProps }: BodyProps) => {
  const {
    id, selected, rect, rotation, mirror, zIndex, hitTest, locked, onSelect, onRepeatClick,
    onContextMenu,
  } = useCanvasObject()
  const {
    register, dragAfterSelect, drag, scale,
  } = useObjectLayer()
  const live = useLiveObject(id)
  const previewed = useSelection((state) => state.previewIds.includes(id))
  const [isHovering, setIsHovering] = useState(false)
  const trackRepeatClick = useRepeatClick(onRepeatClick)

  const ref = useCallback((element: HTMLDivElement | null) => register(id, element), [register, id])

  const handleMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0 || locked) return
    // A miss goes to the canvas, which asks the object again
    if (hitTest && !hitTest(event)) return

    event.stopPropagation()
    if (!selected || event.shiftKey) onSelect?.(id, event.shiftKey)
    if (event.shiftKey) return

    trackRepeatClick(event.nativeEvent)
    if (selected) drag(event.nativeEvent)
    else dragAfterSelect(event.nativeEvent)
  }

  const reactsHere = !hitTest || isHovering
  const mirrored = [
    mirror.horizontal ? "scaleX(-1)" : undefined,
    mirror.vertical ? "scaleY(-1)" : undefined,
  ].filter(Boolean).join(" ")

  return (
    <Box
      ref={ref}
      data-object-id={id}
      position="absolute"
      style={{
        ...pixelBox(rect, scale),
        transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
      }}
      zIndex={zIndex}
      onMouseDown={handleMouseDown}
      onMouseMove={hitTest ? (event) => setIsHovering(hitTest(event)) : undefined}
      onMouseLeave={hitTest ? () => setIsHovering(false) : undefined}
      onContextMenu={onContextMenu}
      cursor={!locked && reactsHere ? "move" : undefined}
      _hover={selected ? { outline: "" } : !previewed && reactsHere ? { outline: "1px solid #C4C4C4" } : {}}
      outline={previewed && !selected ? `${ONE_SCREEN_PIXEL} solid #3182ce` : undefined}
      userSelect="none"
      p={0}
      overflow="visible"
      {...boxProps}
    >
      <Box position="absolute" inset={0} transform={mirrored || undefined}>
        {children(live?.rect ?? rect)}
      </Box>
    </Box>
  )
}

type ResizeProps = {
  /** Always square. */
  square?: boolean
  minSize?: number
}

/** Lets the selected object be resized from its edges and corners. */
export const Resizable = ({ square = false, minSize = 1 }: ResizeProps) => {
  const { id } = useCanvasObject()
  const { setRules } = useObjectLayer()
  useLayoutEffect(() => {
    setRules(id, { resizable: true, square, minSize })

    return () => setRules(id, { resizable: false })
  }, [setRules, id, square, minSize])

  return null
}

/** Lets the selected object be rotated, in 15° steps with Shift. */
export const Rotatable = () => {
  const { id } = useCanvasObject()
  const { setRules } = useObjectLayer()
  useLayoutEffect(() => {
    setRules(id, { rotatable: true })

    return () => setRules(id, { rotatable: false })
  }, [setRules, id])

  return null
}

/** The point 1 unit below the object's lowest corner, rotation included. */
const belowObject = (rect: Rect, rotation: number) => {
  const centerX = rect.x + rect.width / 2
  const centerY = rect.y + rect.height / 2
  if (rotation === 0) return { x: centerX, y: rect.y + rect.height + 1 }

  const radians = (rotation * Math.PI) / 180
  const halfWidth = rect.width / 2
  const halfHeight = rect.height / 2
  const lowest = Math.max(...[
    [-halfWidth, -halfHeight], [halfWidth, -halfHeight],
    [halfWidth, halfHeight], [-halfWidth, halfHeight],
  ].map(([cx, cy]) => centerY + cx * Math.sin(radians) + cy * Math.cos(radians)))

  return { x: centerX, y: lowest + 1 }
}

type LabelProps = {
  /** The label's text; gets the moved or resized rectangle while a gesture is in progress. */
  children: (rect: Rect) => React.ReactNode
}

/** A label centred under the object, kept the same size at any zoom. */
export const CanvasObjectLabel = ({ children }: LabelProps) => {
  const { id, rect, rotation } = useCanvasObject()
  const { scale } = useObjectLayer()
  const live = useLiveObject(id)
  const shown = live?.rect ?? rect
  const position = belowObject(shown, live?.rotation ?? rotation)

  return (
    <Box
      position="absolute"
      left={`${Math.round(position.x * scale)}px`}
      top={`${Math.round(position.y * scale)}px`}
      transform={`translateX(-50%) scale(calc(1 / ${ZOOM}))`}
      transformOrigin="50% 0%"
      fontSize={`${scale * 2}px`}
      color="#3182ce"
      bg="white"
      px={2}
      py={0.5}
      borderRadius="md"
      boxShadow="sm"
      zIndex={10000}
      textAlign="center"
      fontWeight="medium"
      pointerEvents="none"
    >
      {children(shown)}
    </Box>
  )
}

export default CanvasObject
